import {
  SUPPORT_GRANT_DURATION_OPTIONS,
  SUPPORT_MESSAGE_MAX_LENGTH,
  SUPPORT_NOTE_MAX_LENGTH,
  SUPPORT_SUBJECT_MAX_LENGTH,
  decodeAdminSupportRequestDetail,
  decodeSupportGrantEnvelope,
  decodeSupportInbox,
  decodeSupportRequestEnvelope,
  decodeSupportRequestListEnvelope,
  type AdminSupportRequestDetail,
  type SupportAccessGrant,
  type SupportAccessGrantDecision,
  type SupportInbox,
  type SupportRequest,
  type SupportRequestStatus,
} from "../models/supportRequest";
import { apiRequest } from "./functionsClient";

export interface CreateSupportRequestInput {
  subject: string;
  message: string;
  name?: string;
  email?: string;
}

export class SupportInputError extends Error {
  readonly field: "subject" | "message" | "note" | "durationDays";

  constructor(field: SupportInputError["field"], message: string) {
    super(message);
    this.name = "SupportInputError";
    this.field = field;
  }
}

function requiredBoundedString(
  value: string,
  field: "subject" | "message",
  maxLength: number,
): string {
  const cleaned = value.trim();
  if (!cleaned) throw new SupportInputError(field, `${field} is required`);
  if (cleaned.length > maxLength) throw new SupportInputError(field, `${field} is too long`);
  return cleaned;
}

function optionalBoundedString(
  value: string | null | undefined,
  field: "note",
  maxLength: number,
): string | undefined {
  const cleaned = value?.trim();
  if (!cleaned) return undefined;
  if (cleaned.length > maxLength) throw new SupportInputError(field, `${field} is too long`);
  return cleaned;
}

function routeComponent(value: string): string {
  return encodeURIComponent(value);
}

export async function createSupportRequest(
  input: CreateSupportRequestInput,
): Promise<SupportRequest> {
  const body: Record<string, string> = {
    subject: requiredBoundedString(input.subject, "subject", SUPPORT_SUBJECT_MAX_LENGTH),
    message: requiredBoundedString(input.message, "message", SUPPORT_MESSAGE_MAX_LENGTH),
  };
  if (input.name !== undefined) body.name = input.name.trim();
  if (input.email !== undefined) body.email = input.email.trim();

  const response = await apiRequest<unknown>("/support/requests", { method: "POST", body });
  return decodeSupportRequestEnvelope(response);
}

export async function listOwnSupportRequests(): Promise<SupportInbox> {
  const response = await apiRequest<unknown>("/support/requests");
  return decodeSupportInbox(response);
}

export async function addOwnSupportMessage(
  requestID: string,
  body: string,
): Promise<SupportRequest> {
  const response = await apiRequest<unknown>(
    `/support/requests/${routeComponent(requestID)}/messages`,
    {
      method: "POST",
      body: { body: requiredBoundedString(body, "message", SUPPORT_MESSAGE_MAX_LENGTH) },
    },
  );
  return decodeSupportRequestEnvelope(response);
}

export async function resolveOwnSupportRequest(requestID: string): Promise<SupportRequest> {
  const response = await apiRequest<unknown>(
    `/support/requests/${routeComponent(requestID)}/resolve`,
    { method: "POST", body: {} },
  );
  return decodeSupportRequestEnvelope(response);
}

export async function decideSupportAccessGrant(
  requestID: string,
  grantID: string,
  decision: SupportAccessGrantDecision,
): Promise<SupportAccessGrant> {
  const response = await apiRequest<unknown>(
    `/support/requests/${routeComponent(requestID)}/grants/${routeComponent(grantID)}/decide`,
    { method: "POST", body: { decision } },
  );
  return decodeSupportGrantEnvelope(response);
}

export async function revokeSupportAccessGrant(
  requestID: string,
  grantID: string,
): Promise<SupportAccessGrant> {
  const response = await apiRequest<unknown>(
    `/support/requests/${routeComponent(requestID)}/grants/${routeComponent(grantID)}/revoke`,
    { method: "POST", body: {} },
  );
  return decodeSupportGrantEnvelope(response);
}

export async function listAdminSupportRequests(
  status?: SupportRequestStatus,
): Promise<SupportRequest[]> {
  const response = await apiRequest<unknown>("/admin/support/requests/list", {
    method: "POST",
    body: status ? { status } : {},
  });
  return decodeSupportRequestListEnvelope(response);
}

export async function getAdminSupportRequestDetail(
  requestID: string,
): Promise<AdminSupportRequestDetail> {
  const response = await apiRequest<unknown>("/admin/support/requests/detail", {
    method: "POST",
    body: { requestID },
  });
  return decodeAdminSupportRequestDetail(response);
}

export async function addAdminSupportReply(
  requestID: string,
  body: string,
): Promise<SupportRequest> {
  const response = await apiRequest<unknown>(
    `/admin/support/requests/${routeComponent(requestID)}/reply`,
    {
      method: "POST",
      body: { body: requiredBoundedString(body, "message", SUPPORT_MESSAGE_MAX_LENGTH) },
    },
  );
  return decodeSupportRequestEnvelope(response);
}

export async function setAdminSupportRequestResolved(
  requestID: string,
  resolved: boolean,
): Promise<SupportRequest> {
  const response = await apiRequest<unknown>(
    `/admin/support/requests/${routeComponent(requestID)}/resolve`,
    { method: "POST", body: { resolved } },
  );
  return decodeSupportRequestEnvelope(response);
}

export async function requestAdminSupportAccess(
  requestID: string,
  durationDays: number,
  note?: string | null,
): Promise<SupportAccessGrant> {
  if (
    !Number.isInteger(durationDays)
    || !SUPPORT_GRANT_DURATION_OPTIONS.includes(
      durationDays as (typeof SUPPORT_GRANT_DURATION_OPTIONS)[number],
    )
  ) {
    throw new SupportInputError(
      "durationDays",
      `durationDays must be one of ${SUPPORT_GRANT_DURATION_OPTIONS.join(", ")}`,
    );
  }
  const cleanedNote = optionalBoundedString(note, "note", SUPPORT_NOTE_MAX_LENGTH);
  const response = await apiRequest<unknown>(
    `/admin/support/requests/${routeComponent(requestID)}/request-access`,
    {
      method: "POST",
      body: {
        durationDays,
        ...(cleanedNote ? { note: cleanedNote } : {}),
      },
    },
  );
  return decodeSupportGrantEnvelope(response);
}
